import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "address", "zipCode", "city", "province", "floor" ]

  connect() {
    this.loadGoogleMapsAPI().then(() => {
      this.initializeAutocomplete();
    }).catch((error) => {
      // Error loading Google Maps API
    });
  }

  loadGoogleMapsAPI() {
    return new Promise((resolve, reject) => {
      // Verificar si ya está cargado y completamente disponible
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete) {
        resolve();
        return;
      }

      const apiKey = this.getGoogleApiKey();
      if (!apiKey) {
        reject('No se encontró la API key de Google Maps');
        return;
      }

      // Cargar con loading=async para mejor rendimiento (según best practices de Google)
      // El parámetro loading=async en la URL evita el warning de rendimiento
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        // Esperar a que google.maps.places esté disponible
        this.waitForPlacesLibrary(resolve, reject);
      };
      script.onerror = (error) => {
        reject('Error al cargar Google Maps API');
      };

      document.head.appendChild(script);
    });
  }

  waitForPlacesLibrary(resolve, reject, attempts = 0) {
    const maxAttempts = 50; // 5 segundos máximo (50 * 100ms)

    if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete) {
      resolve();
      return;
    }

    if (attempts >= maxAttempts) {
      reject('Google Maps Places API no está disponible después de cargar el script');
      return;
    }

    // Reintentar después de 100ms
    setTimeout(() => {
      this.waitForPlacesLibrary(resolve, reject, attempts + 1);
    }, 100);
  }

  getGoogleApiKey() {
    const metaTag = document.querySelector('meta[name="google-maps-api-key"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }
    if (window.GOOGLE_MAPS_API_KEY) {
      return window.GOOGLE_MAPS_API_KEY;
    }
    return null;
  }

  initializeAutocomplete() {
    const addressInput = this.addressTarget;

    if (!addressInput) {
      return;
    }

    // Verificar que Google Maps esté cargado y que Autocomplete esté disponible
    if (!window.google || !window.google.maps || !window.google.maps.places || !window.google.maps.places.Autocomplete) {
      return;
    }

    // Usar PlaceAutocompleteElement (recomendado) si está disponible, sino usar Autocomplete (legacy)
    if (window.google.maps.places.PlaceAutocompleteElement) {
      this.initializePlaceAutocompleteElement(addressInput);
    } else {
      // Fallback a Autocomplete legacy si PlaceAutocompleteElement no está disponible
      this.initializeLegacyAutocomplete(addressInput);
    }
  }

  initializePlaceAutocompleteElement(addressInput) {
    // PlaceAutocompleteElement es un web component que requiere un enfoque diferente
    // Por ahora, mantenemos Autocomplete pero con loading=async para evitar el warning
    // La migración completa a PlaceAutocompleteElement requiere cambios más extensos en el HTML
    // Por simplicidad y compatibilidad, usamos Autocomplete con loading=async
    this.initializeLegacyAutocomplete(addressInput);
  }

  initializeLegacyAutocomplete(addressInput) {
    let autocompleteAddress;
    try {
      // Usar Autocomplete legacy como fallback
      autocompleteAddress = new google.maps.places.Autocomplete(addressInput, {
        componentRestrictions: { country: ["arg"] },
        fields: ["address_components", "geometry"],
        types: ["address"],
      });

      // Prevenir que Enter envíe el formulario SOLO cuando hay una sugerencia visible del autocomplete
      google.maps.event.addDomListener(addressInput, 'keydown', (e) => {
        if (e.key === "Enter") {
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer && pacContainer.style.display !== 'none') {
            e.preventDefault();
          }
        }
      });

      // Manejar cuando se selecciona un lugar del autocomplete
      autocompleteAddress.addListener("place_changed", () => {
      const place = autocompleteAddress.getPlace();

      if (!place.address_components) {
        return;
      }

      let postcode = "";

      for (const component of place.address_components) {
        const componentType = component.types[0];
        switch (componentType) {
          case "postal_code": {
            postcode = `${component.long_name}${postcode}`;
            break;
          }
          case "postal_code_suffix": {
            postcode = `${postcode}-${component.long_name}`;
            break;
          }
          case "locality":
            if (this.hasCityTarget) {
              this.cityTarget.value = component.long_name;
            }
            break;
          case "administrative_area_level_1": {
            if (this.hasProvinceTarget) {
              this.provinceTarget.value = component.short_name;
            }
            break;
          }
        }
      }

      if (this.hasZipCodeTarget) {
        this.zipCodeTarget.value = postcode;
      }

      if (this.hasFloorTarget) {
        this.floorTarget.focus();
      }
    });
    } catch (error) {
      // Error initializing Autocomplete
    }
  }
}
